import React, { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasDashboardAccess } from '../api/profile';
import { BurgerButton, SideDrawer, useSideDrawer } from '../shared/ui';
import SignInModal from './SignInModal';
import bmcsLogo from '../assets/images/logo/bmcs-transparent.png';

const PUBLIC_DRAWER_ID = 'public-site-drawer';

const navItems = [
  { path: '/', label: 'Home', icon: 'bi-house', end: true },
  { path: '/about', label: 'About', icon: 'bi-info-circle' },
  { path: '/masjid', label: 'Masjid', icon: 'bi-building' },
  { path: '/classes', label: 'Classes', icon: 'bi-book' },
  { path: '/contact', label: 'Contact', icon: 'bi-telephone' },
];

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, logout } = useAuth();
  const { open: menuOpen, setOpen, toggleDrawer, closeDrawer } = useSideDrawer(location.pathname);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  const canOpenPortal = Boolean(user && hasDashboardAccess(profile));

  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile drawer when switching to laptop/desktop widths.
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const onChange = () => {
      if (mq.matches) setOpen(false);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [setOpen]);

  const isActive = (path: string, end?: boolean) => {
    if (end || path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    closeDrawer();
    await logout();
    navigate('/');
  };

  return (
    <>
      <nav
        className={`navbar navbar-dark bg-gradient-dark sticky-top shadow-lg public-navbar ${
          isScrolled ? 'scrolled' : ''
        }`}
      >
        <div className="container public-navbar__inner">
          <Link className="navbar-brand fw-bold d-flex align-items-center text-nowrap mb-0" to="/">
            <div className="me-2 flex-shrink-0">
              <img
                src={bmcsLogo}
                alt="BMCS Logo"
                className="navbar-logo"
                style={{ height: '36px', width: 'auto' }}
              />
            </div>
            <span className="d-none d-md-inline fw-bold fs-5">
              Bothasig Muslim and Cultural Society
            </span>
            <span className="d-md-none fw-bold fs-6">BMCS</span>
          </Link>

          <div className="public-navbar__actions">
            <ul className="navbar-nav public-navbar__links d-none d-lg-flex flex-row align-items-center mb-0">
              {navItems.map((item) => (
                <li className="nav-item" key={item.path}>
                  <Link
                    className={`nav-link btn btn-link text-white position-relative ${
                      isActive(item.path, item.end) ? 'active' : ''
                    }`}
                    to={item.path}
                  >
                    <i className={`${item.icon} me-2`} />
                    {item.label}
                    {isActive(item.path, item.end) && (
                      <span
                        className="position-absolute bottom-0 start-50 translate-middle-x bg-warning rounded-pill"
                        style={{ width: '20px', height: '3px' }}
                      />
                    )}
                  </Link>
                </li>
              ))}
            </ul>

            {canOpenPortal ? (
              <Link
                className="btn btn-warning btn-sm px-3 py-2 text-dark fw-semibold d-none d-lg-inline-flex align-items-center"
                to="/dashboard"
              >
                <i className="bi bi-grid-1x2-fill me-2" aria-hidden />
                Portal
              </Link>
            ) : user ? null : (
              <button
                type="button"
                className="btn btn-outline-light btn-sm px-3 py-2 d-none d-lg-inline-flex align-items-center"
                onClick={() => setShowSignInModal(true)}
              >
                <i className="bi bi-person-circle me-2" aria-hidden />
                Sign In
              </button>
            )}

            <div className="d-lg-none">
              <BurgerButton
                open={menuOpen}
                onClick={toggleDrawer}
                controlsId={PUBLIC_DRAWER_ID}
              />
            </div>
          </div>
        </div>
      </nav>

      <SideDrawer
        id={PUBLIC_DRAWER_ID}
        open={menuOpen}
        onClose={closeDrawer}
        ariaLabel="Site menu"
        header={
          <>
            <div>
              <p className="side-drawer__title">BMCS</p>
              <p className="side-drawer__subtitle">
                {user ? profile?.name || user.email : 'Main menu'}
              </p>
            </div>
            <button
              type="button"
              className="side-drawer__close"
              aria-label="Close menu"
              onClick={closeDrawer}
            >
              <i className="bi bi-x-lg" />
            </button>
          </>
        }
        footer={
          user ? (
            <>
              {canOpenPortal ? (
                <Link
                  to="/dashboard"
                  className="dashboard-sidebar__home"
                  title="Open portal"
                  aria-label="Portal"
                  onClick={closeDrawer}
                >
                  <i className="bi bi-grid-1x2-fill" aria-hidden />
                </Link>
              ) : null}
              <button type="button" className="dashboard-sidebar__signout" onClick={handleSignOut}>
                <i className="bi bi-box-arrow-right" aria-hidden />
                <span>Sign Out</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="side-drawer__action side-drawer__action--accent"
              onClick={() => {
                closeDrawer();
                setShowSignInModal(true);
              }}
            >
              <i className="bi bi-person-circle" aria-hidden />
              <span>Sign In</span>
            </button>
          )
        }
      >
        <nav className="public-drawer__nav" aria-label="Site">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive: active }) =>
                ['side-drawer__link', active ? 'side-drawer__link--active' : '']
                  .filter(Boolean)
                  .join(' ')
              }
              onClick={closeDrawer}
            >
              <i className={`bi ${item.icon}`} aria-hidden />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </SideDrawer>

      <SignInModal
        show={showSignInModal}
        onHide={() => setShowSignInModal(false)}
        onSignInSuccess={() => {
          setShowSignInModal(false);
          navigate('/dashboard');
        }}
      />
    </>
  );
};

export default Navigation;
